/**
 * usePageCopy — hook público para leer slots de `pages_copy`.
 *
 * Patrón de uso en cualquier componente público:
 *   const title = usePageCopy('home', 'hero.title', 'Arte que sana.');
 *   <h1>{title}</h1>
 *
 * Comportamiento:
 *  - Mientras el query está cargando: devuelve `fallback` directamente
 *    (cero loading state visible en UI; los textos no parpadean).
 *  - Si el slot existe en BD y `deleted_at is null`: devuelve `value_md`.
 *  - Si no existe en BD o hay error: devuelve `fallback`.
 *
 * Cache: query key `['pages_copy', page_slug, locale]` con `staleTime` largo
 * (5 min) porque los textos cambian poco; la mutation admin invalida ambas
 * keys para refrescar al instante.
 *
 * Esto evita N queries (una por slot) — todos los slots de una página
 * comparten el mismo fetch. El hook hace lookup local sobre el array.
 */
import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase/client';
import type { Database } from '../../lib/supabase/types';

type PageCopyRow = Database['public']['Tables']['pages_copy']['Row'];

const DEFAULT_LOCALE = 'es';

/**
 * Trae todos los slots vivos para una página en un locale. Hook interno;
 * los componentes usan `usePageCopy(slug, key, fallback)` o
 * `usePageCopyMap(slug)`.
 */
function usePageCopyQuery(pageSlug: string, locale: string) {
  return useQuery<PageCopyRow[], Error>({
    queryKey: ['pages_copy', pageSlug, locale],
    queryFn: async (): Promise<PageCopyRow[]> => {
      const { data, error } = await supabase
        .from('pages_copy')
        .select('*')
        .eq('page_slug', pageSlug)
        .eq('locale', locale)
        .is('deleted_at', null);
      if (error) throw new Error(error.message);
      return (data as PageCopyRow[] | null) ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Devuelve el valor BD del slot o el fallback. **No es nullable** — siempre
 * devuelve un string, así los componentes no necesitan defaults extra.
 */
export function usePageCopy(
  pageSlug: string,
  copyKey: string,
  fallback: string,
  locale: string = DEFAULT_LOCALE,
): string {
  const { data } = usePageCopyQuery(pageSlug, locale);
  if (!data) return fallback;
  const row = data.find((r) => r.copy_key === copyKey);
  return row?.value_md ?? fallback;
}

/**
 * Variante "bulk": devuelve un Map<copyKey, value_md> para resolver muchos
 * slots de la misma página sin disparar múltiples queries. Útil cuando un
 * componente consume 5+ slots a la vez (ej. el hero de Terapias).
 *
 * Conviene combinarlo con un objeto `FALLBACKS` local para garantizar que
 * el componente nunca renderice undefined.
 */
export function usePageCopyMap(
  pageSlug: string,
  locale: string = DEFAULT_LOCALE,
): Map<string, string> {
  const { data } = usePageCopyQuery(pageSlug, locale);
  const m = new Map<string, string>();
  if (!data) return m;
  for (const row of data) m.set(row.copy_key, row.value_md);
  return m;
}
